import { useState, useRef, useCallback, useMemo, useEffect } from 'react'
import { useParams, Link as RouterLink } from 'react-router-dom'
import { Box, Typography, Alert, Breadcrumbs, Link, ToggleButtonGroup, ToggleButton } from '@mui/material'
import { TableChart, NavigateNext } from '@mui/icons-material'
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'

ModuleRegistry.registerModules([AllCommunityModule])

import { getColumnDefs, defaultColDef, gridOptions, MONTHS } from '../components/planningboard/GridConfig'
import { DOSCellRenderer, CasesCellRenderer, IssueCellRenderer } from '../components/planningboard/CellRenderers'
import FilterBar from '../components/planningboard/FilterBar'
import SearchBar from '../components/planningboard/SearchBar'
import ModificationsPanel from '../components/planningboard/ModificationsPanel'
import LineSummary from '../components/planningboard/LineSummary'
import ContextMenu from '../components/planningboard/ContextMenu'
import OverrideBottomSheet from '../components/planningboard/OverrideBottomSheet'
import LoadingState from '../components/common/LoadingState'
import { useApi } from '../hooks/useApi'
import { usePlantContext } from '../hooks/usePlantContext'
import { calculateRequiredHours, calculateRunRate } from '../utils/dos'
import { applyModifications } from '../utils/applyModifications'

// ─── fallback demo data — used when API is unavailable or returns empty ──────

const DEMO_ROWS = [
  {
    itemNumber: '122216040112',
    description: 'MG CHILI BNS 12/15',
    briteCode: '12221',
    line: 1,
    abcClass: 'A',
    dosMin: 14,
    dosMax: 45,
    autoInv: true,
    percentOfMinInv: 1.12,
    hasIssue: true,
    cpm: 400, efficiency: 0.85, packSize: 12,
    apr26_dos: 8.2, apr26_fcst: 4200, apr26_cases: 5600, apr26_hrs: 19.8,
    may26_dos: 18.5, may26_fcst: 3800, may26_cases: 3200, may26_hrs: 11.3,
    jun26_dos: 22.1, jun26_fcst: 4100, jun26_cases: 4000, jun26_hrs: 14.1,
    jul26_dos: 15.3, jul26_fcst: 4500, jul26_cases: 4800, jul26_hrs: 16.9,
    aug26_dos: 12.0, aug26_fcst: 4000, aug26_cases: 3600, aug26_hrs: 12.7,
    sep26_dos: 25.4, sep26_fcst: 3600, sep26_cases: 4200, sep26_hrs: 14.8,
  },
  {
    itemNumber: '123211020112',
    description: 'HV CHILI BNS TP 12/15.5 OZ',
    briteCode: '12321',
    line: 2,
    abcClass: 'A',
    dosMin: 21,
    dosMax: 60,
    autoInv: true,
    percentOfMinInv: 1.12,
    hasIssue: false,
    cpm: 350, efficiency: 0.82, packSize: 12,
    apr26_dos: 32.6, apr26_fcst: 2800, apr26_cases: 2600, apr26_hrs: 10.9,
    may26_dos: 28.9, may26_fcst: 3100, may26_cases: 3000, may26_hrs: 12.5,
    jun26_dos: 45.2, jun26_fcst: 2500, jun26_cases: 2200, jun26_hrs: 9.2,
    jul26_dos: 38.7, jul26_fcst: 2900, jul26_cases: 3100, jul26_hrs: 12.9,
    aug26_dos: 62.1, aug26_fcst: 2200, aug26_cases: 1800, aug26_hrs: 7.5,
    aug26_scenarioModified: true,
    sep26_dos: 41.3, sep26_fcst: 2600, sep26_cases: 2800, sep26_hrs: 11.7,
  },
  {
    itemNumber: '123216050112',
    description: 'FINEST CHILI BNS TP 12/15.5 OZ',
    briteCode: '12321',
    line: 2,
    abcClass: 'B',
    dosMin: 14,
    dosMax: 45,
    autoInv: true,
    percentOfMinInv: 1.12,
    hasIssue: true,
    cpm: 350, efficiency: 0.82, packSize: 12,
    apr26_dos: 11.3, apr26_fcst: 1800, apr26_cases: 2200, apr26_hrs: 9.2,
    may26_dos: 19.8, may26_fcst: 1600, may26_cases: 1400, may26_hrs: 5.8,
    jun26_dos: 6.2, jun26_fcst: 2100, jun26_cases: 1200, jun26_hrs: 5.0,
    jul26_dos: 28.4, jul26_fcst: 1500, jul26_cases: 2800, jul26_hrs: 11.7,
    aug26_dos: 34.1, aug26_fcst: 1200, aug26_cases: 1000, aug26_hrs: 4.2,
    sep26_dos: 16.5, sep26_fcst: 1700, sep26_cases: 1600, sep26_hrs: 6.7,
  },
  {
    itemNumber: '124216040112',
    description: 'MG TX MX CHILI BN 12/15',
    briteCode: '12421',
    line: 1,
    abcClass: 'B',
    dosMin: 14,
    dosMax: 45,
    autoInv: false,
    percentOfMinInv: 1.0,
    hasIssue: false,
    cpm: 400, efficiency: 0.85, packSize: 12,
    apr26_dos: 42.8, apr26_fcst: 900, apr26_cases: 600, apr26_hrs: 2.1,
    may26_dos: 35.1, may26_fcst: 1100, may26_cases: 1000, may26_hrs: 3.5,
    jun26_dos: 0, jun26_fcst: 0, jun26_cases: 0, jun26_hrs: 0,
    jul26_dos: 29.6, jul26_fcst: 800, jul26_cases: 900, jul26_hrs: 3.2,
    aug26_dos: 22.3, aug26_fcst: 1000, aug26_cases: 1100, aug26_hrs: 3.9,
    aug26_override: true,
    sep26_dos: 18.9, sep26_fcst: 1200, sep26_cases: 1400, sep26_hrs: 4.9,
  },
  {
    itemNumber: '123210490112',
    description: 'CV CHILI BNS 12/15.5 OZ',
    briteCode: '12321',
    line: 3,
    abcClass: 'C',
    dosMin: 21,
    dosMax: 60,
    autoInv: true,
    percentOfMinInv: 1.12,
    hasIssue: false,
    cpm: 300, efficiency: 0.78, packSize: 12,
    apr26_dos: 55.3, apr26_fcst: 600, apr26_cases: 400, apr26_hrs: 2.1,
    may26_dos: 48.7, may26_fcst: 700, may26_cases: 650, may26_hrs: 3.3,
    jun26_dos: -2.1, jun26_fcst: 900, jun26_cases: 200, jun26_hrs: 1.0,
    jul26_dos: 38.2, jul26_fcst: 500, jul26_cases: 800, jul26_hrs: 4.1,
    aug26_dos: 44.6, aug26_fcst: 550, aug26_cases: 500, aug26_hrs: 2.6,
    sep26_dos: 61.8, sep26_fcst: 400, sep26_cases: 350, sep26_hrs: 1.8,
  },
]

// ─── Component ───────────────────────────────────────────────────────────────

export default function PlanningBoard() {
  const gridRef = useRef(null)
  const { planId } = useParams()
  const { loading, error, get, put } = useApi()
  const { plant, planContext, scenarioContext } = usePlantContext()

  const DEMO_MODS = useMemo(() => [
    {
      id: 'demo-mod-1', planId: 'demo', listPosition: 1,
      name: 'Brite 12221 — Full Season +30%',
      scope: 'BriteCode', identifier: '12221',
      changeType: 'PercentOfMinInv', defaultValue: 1.30,
      monthValues: {}, isIgnored: false,
      createdBy: 'melody.simon@faribaultfoods.com', createdAt: '2026-03-09T09:45:00Z',
    },
    {
      id: 'demo-mod-2', planId: 'demo', listPosition: 2,
      name: 'All Items — Safety Stock +500',
      scope: 'AllProduct', identifier: '',
      changeType: 'Qty', defaultValue: 500,
      monthValues: {}, isIgnored: false,
      createdBy: 'melody.simon@faribaultfoods.com', createdAt: '2026-03-09T10:00:00Z',
    },
    {
      id: 'demo-mod-3', planId: 'demo', listPosition: 3,
      name: 'CV CHILI — Remove Apr production',
      scope: 'ItemNumber', identifier: '123210490112',
      changeType: 'Qty', defaultValue: null,
      monthValues: { '2026-04': -400, '2026-06': -200 }, isIgnored: false,
      createdBy: 'melody.simon@faribaultfoods.com', createdAt: '2026-03-09T10:30:00Z',
    },
  ], [])

  const [baseRowData, setBaseRowData] = useState(DEMO_ROWS)
  const [modifications, setModifications] = useState(DEMO_MODS)
  const [displayMode, setDisplayMode] = useState('modified')
  const [initialLoad, setInitialLoad] = useState(true)
  const [planName, setPlanName] = useState('')

  const rowData = useMemo(
    () => applyModifications(baseRowData, modifications, displayMode),
    [baseRowData, modifications, displayMode],
  )
  const [filters, setFilters] = useState({
    line: '',
    briteCode: '',
    abcClass: '',
    issueStatus: '',
    autoInv: '',
  })
  const [adjustments, setAdjustments] = useState([])
  const [contextMenu, setContextMenu] = useState({ open: false, position: null, rowData: null })
  const [overrideSheet, setOverrideSheet] = useState({ open: false, editContext: null })

  const activePlanId = planId || planContext?.id

  useEffect(() => {
    let cancelled = false
    const query = new URLSearchParams({ plant: plant.plantCode })
    if (activePlanId) query.set('planId', activePlanId)
    get(`/board?${query}`)
      .then((data) => {
        if (cancelled) return
        const rows = Array.isArray(data) ? data : data?.rows
        if (rows?.length) setBaseRowData(rows)
      })
      .catch(() => {
        // API unavailable — keep fallback demo data
      })
      .finally(() => {
        if (!cancelled) setInitialLoad(false)
      })
    return () => { cancelled = true }
  }, [plant.plantCode, activePlanId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!activePlanId) return
    let cancelled = false
    get(`/plans/${activePlanId}?plant=${plant.plantCode}`)
      .then((data) => {
        if (!cancelled && data?.name) setPlanName(data.name)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [activePlanId, plant.plantCode]) // eslint-disable-line react-hooks/exhaustive-deps

  const briteCodeOptions = useMemo(
    () => [...new Set(rowData.map((r) => r.briteCode))].sort(),
    [rowData],
  )

  const columnDefs = useMemo(() => getColumnDefs(), [])
  const components = useMemo(
    () => ({
      dosCellRenderer: DOSCellRenderer,
      casesCellRenderer: CasesCellRenderer,
      issueCellRenderer: IssueCellRenderer,
    }),
    [],
  )

  // ─── External filter ────────────────────────────────────────────────────

  const isExternalFilterPresent = useCallback(
    () => Object.values(filters).some((v) => v !== ''),
    [filters],
  )

  const doesExternalFilterPass = useCallback(
    (node) => {
      const d = node.data
      if (filters.line && String(d.line) !== filters.line) return false
      if (filters.briteCode && d.briteCode !== filters.briteCode) return false
      if (filters.abcClass && d.abcClass !== filters.abcClass) return false
      if (filters.issueStatus) {
        if (filters.issueStatus === 'issues' && !d.hasIssue) return false
        if (filters.issueStatus === 'clear' && d.hasIssue) return false
      }
      if (filters.autoInv) {
        if (filters.autoInv === 'yes' && !d.autoInv) return false
        if (filters.autoInv === 'no' && d.autoInv) return false
      }
      return true
    },
    [filters],
  )

  useEffect(() => {
    gridRef.current?.api?.onFilterChanged()
  }, [filters])

  // ─── Cell editing → scenario adjustments ────────────────────────────────

  const onCellValueChanged = useCallback(
    (params) => {
      if (!params.colDef.field.endsWith('_cases')) return
      const monthKey = params.colDef.field.replace('_cases', '')
      const monthLabel = MONTHS.find((m) => m.key === monthKey)?.label || monthKey

      const adj = {
        itemNumber: params.data.itemNumber,
        description: params.data.description,
        month: monthLabel,
        field: 'CASES',
        oldValue: params.oldValue,
        newValue: params.newValue,
      }

      setAdjustments((prev) => [...prev, adj])

      const runRate = calculateRunRate(params.data.cpm, params.data.efficiency, params.data.packSize)
      const hrs = calculateRequiredHours(params.newValue, runRate)
      params.node.setDataValue(`${monthKey}_hrs`, hrs)
      params.node.setDataValue(`${monthKey}_scenarioModified`, true)

      put('/board/cell', {
        materialNumber: params.data.itemNumber,
        month: monthLabel,
        scheduledCases: params.newValue,
        rationale: adj.description,
        planVersionId: activePlanId,
        scenarioId: scenarioContext?.id,
      }).catch(() => {
        // persist failed silently — local state is still updated
      })

      if (scenarioContext) {
        setOverrideSheet({
          open: true,
          editContext: {
            ...adj,
            monthLabel,
          },
        })
      }
    },
    [scenarioContext, activePlanId, put],
  )

  // ─── Context menu ──────────────────────────────────────────────────────

  const onCellContextMenu = useCallback((params) => {
    params.event.preventDefault()
    setContextMenu({
      open: true,
      position: { top: params.event.clientY, left: params.event.clientX },
      rowData: params.data,
    })
  }, [])

  const handleContextAction = useCallback((action, row) => {
    // Placeholder for action handlers
    console.info(`Action: ${action}`, row)
  }, [])

  const handleRemoveAdjustment = useCallback((index) => {
    setAdjustments((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const handleOverrideSave = useCallback((overrideData) => {
    console.info('Override saved:', overrideData)
  }, [])

  // ─── Render ────────────────────────────────────────────────────────────

  if (initialLoad && loading) return <LoadingState message="Loading planning board..." />

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 1.5 }}>
      {/* Breadcrumb */}
      <Breadcrumbs separator={<NavigateNext sx={{ fontSize: 16 }} />} sx={{ fontSize: '0.85rem' }}>
        <Link component={RouterLink} to="/plans" underline="hover" color="text.secondary">
          Plans
        </Link>
        <Typography color="text.primary" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
          {planName || 'Planning Board'}
        </Typography>
      </Breadcrumbs>

      {/* Page header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TableChart color="primary" sx={{ fontSize: 22 }} />
        <Typography variant="h3" sx={{ flex: 1 }}>Planning Board</Typography>
        <ToggleButtonGroup
          value={displayMode}
          exclusive
          onChange={(_, v) => v && setDisplayMode(v)}
          size="small"
          sx={{ '& .MuiToggleButton-root': { fontSize: '0.7rem', px: 1.5, py: 0.25, textTransform: 'none' } }}
        >
          <ToggleButton value="modified">Modified</ToggleButton>
          <ToggleButton value="base">Base Plan</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary">
          {rowData.length} items · {plant.name}
        </Typography>
      </Box>

      {error && (
        <Alert severity="info" sx={{ py: 0.5 }}>
          Using demo data — API unavailable ({error})
        </Alert>
      )}

      {/* Modifications Panel (replaces ZoneA + ZoneB) */}
      <ModificationsPanel
        planId={activePlanId}
        modifications={modifications}
        onModificationsChange={setModifications}
      />

      {/* Line Summary */}
      <LineSummary rowData={rowData} />

      {/* Zone C: Sticky header — filters + search */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2,
        py: 1,
        backgroundColor: 'surface.containerLow',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        flexWrap: 'wrap',
      }}>
        <FilterBar
          filters={filters}
          onFilterChange={setFilters}
          briteCodeOptions={briteCodeOptions}
        />
        <Box sx={{ flex: 1 }} />
        <SearchBar gridRef={gridRef} />
      </Box>

      {/* Zone D: AG Grid */}
      <Box
        sx={{
          flex: 1,
          minHeight: 400,
          width: '100%',
          '& .ag-header-group-cell': {
            fontWeight: 700,
            fontSize: '0.75rem',
            borderBottom: '2px solid',
            borderBottomColor: 'divider',
          },
          '& .ag-header-cell': {
            fontSize: '0.75rem',
          },
          '& .ag-cell': {
            borderRight: '1px solid #E1E8E1',
          },
          '& .ag-row-even': {
            backgroundColor: '#FBFDFB',
          },
          '& .ag-row-odd': {
            backgroundColor: '#F4F7F4',
          },
          '& .ag-row:hover': {
            backgroundColor: '#EEF3EE !important',
          },
          '& .ag-pinned-left-cols-container .ag-cell': {
            backgroundColor: 'inherit',
          },
          '& .ag-pinned-left-header': {
            borderRight: '2px solid #C5CFC5',
          },
          '& .ag-pinned-left-cols-container': {
            borderRight: '2px solid #C5CFC5',
          },
        }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          components={components}
          rowHeight={gridOptions.rowHeight}
          headerHeight={gridOptions.headerHeight}
          groupHeaderHeight={gridOptions.groupHeaderHeight}
          animateRows={gridOptions.animateRows}
          suppressCellFocus={gridOptions.suppressCellFocus}
          enableCellTextSelection={gridOptions.enableCellTextSelection}
          rowSelection={gridOptions.rowSelection}
          getRowId={gridOptions.getRowId}
          rowModelType="clientSide"
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
          onCellValueChanged={onCellValueChanged}
          onCellContextMenu={onCellContextMenu}
          tooltipShowDelay={300}
          domLayout="normal"
        />
      </Box>

      {/* Context menu */}
      <ContextMenu
        open={contextMenu.open}
        anchorPosition={contextMenu.position}
        onClose={() => setContextMenu({ open: false, position: null, rowData: null })}
        onAction={handleContextAction}
        rowData={contextMenu.rowData}
      />

      {/* Override bottom sheet */}
      <OverrideBottomSheet
        open={overrideSheet.open}
        onClose={() => setOverrideSheet({ open: false, editContext: null })}
        onSave={handleOverrideSave}
        editContext={overrideSheet.editContext}
      />
    </Box>
  )
}
